import { DAY_OF_WEEK } from '../enums';
import { SafeRegexPatternSchema } from '../validation/regexPattern';
import { GlossarySettingsSchema } from './glossary';
import { AutoReplySettingsSchema } from './originator';
import {
  CoverSettingsSchema,
  isValidIanaTimezone,
  NotificationSettingsSchema,
  TrackOrganizationSettingsSchema,
} from './organization';
import { RateLimitSchema } from './rateLimit';
import { QuoteSettingsSchema } from './quote';
import { z } from 'zod';

export enum QuoteAutoReplyScope {
  DISABLED = 'DISABLED',
  DRAFT = 'DRAFT',
  SEND = 'SEND',
  AGENT = 'AGENT',
}

export { TeamMemberRole, TeamMemberRoleSchema } from './teamMemberRole';

const WeeklyTimeSchema = z.object({
  day: z.enum(DAY_OF_WEEK),
  time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
});

export const AfterHoursWindowSchema = z.object({
  start: WeeklyTimeSchema,
  end: WeeklyTimeSchema,
});
export type AfterHoursWindow = z.infer<typeof AfterHoursWindowSchema>;

export const AFTER_HOURS_TRANSFER_PHONE_NUMBER_REGEX = /^\+1\d{10}$/;

export const AfterHoursSettingsSchema = z.object({
  email: z.string().email().optional().nullable(),
  transferPhoneNumber: z
    .string()
    .regex(AFTER_HOURS_TRANSFER_PHONE_NUMBER_REGEX)
    .optional()
    .nullable(),
  timezone: z
    .string()
    .trim()
    .min(1)
    .refine(isValidIanaTimezone, { message: 'Must be a valid IANA timezone.' })
    .default('America/Chicago'),
  alwaysOn: z.boolean().default(false),
  windows: z.array(AfterHoursWindowSchema).default([]),
});
export type AfterHoursSettings = z.infer<typeof AfterHoursSettingsSchema>;

export const TeamIdentifierRuleGroupSchema = z
  .record(z.string().min(1), SafeRegexPatternSchema)
  .refine((group) => Object.keys(group).length > 0, {
    message: 'Team identifier rule group must include at least one field.',
  });

export const TeamIdentifierRulesSchema = z.array(TeamIdentifierRuleGroupSchema);

export type TeamIdentifierRuleGroup = z.infer<
  typeof TeamIdentifierRuleGroupSchema
>;
export type TeamIdentifierRules = z.infer<typeof TeamIdentifierRulesSchema>;

const LEGACY_TEAM_IDENTIFIER_REGEX_KEY = 'teamIdentifierRegex';
const LEGACY_ALJEX_TEAM_IDENTIFIER_FIELD_PATH = 'originatorTeamIdentifier';

function normalizeLegacyTeamIdentifierRuleGroup(
  group: TeamIdentifierRuleGroup
): TeamIdentifierRuleGroup {
  const legacyRegex = group[LEGACY_TEAM_IDENTIFIER_REGEX_KEY];
  if (legacyRegex !== undefined && Object.keys(group).length === 1) {
    return { [LEGACY_ALJEX_TEAM_IDENTIFIER_FIELD_PATH]: legacyRegex };
  }
  return group;
}

export const TeamSettingsSchema = z
  .object({
    // NOTE(parlato): Ideally this would live in the autoReply object; I am keeping
    // it here to maintain the shape of Originator settings; quoteAutoReplyScope is
    // a column on Originator
    quoteAutoReplyScope: z
      .nativeEnum(QuoteAutoReplyScope)
      .optional()
      .nullable(),
    afterHours: AfterHoursSettingsSchema.optional().nullable(),
    autoReply: AutoReplySettingsSchema.optional().nullable(),
    cover: CoverSettingsSchema.optional().nullable(),
    glossary: GlossarySettingsSchema.optional().nullable(),
    track: TrackOrganizationSettingsSchema.optional().nullable(),
    transfersRateLimit: RateLimitSchema.optional().nullable(),
    notifications: NotificationSettingsSchema.optional().nullable(),
    quote: QuoteSettingsSchema.optional().nullable(),
    teamIdentifierRules: TeamIdentifierRulesSchema.optional().nullable(),
  })
  .strict();
export type TeamSettings = z.infer<typeof TeamSettingsSchema>;

type TeamSettingsWithLegacyRules = TeamSettings & {
  aljexTeamIdentifierRules?: TeamIdentifierRules;
};

export function getTeamIdentifierRulesFromTeamSettings(
  settings: TeamSettings | null | undefined
): TeamIdentifierRules {
  if (!settings) return [];

  const legacySettings = settings as TeamSettingsWithLegacyRules;
  const rawRules =
    legacySettings.teamIdentifierRules ??
    legacySettings.aljexTeamIdentifierRules;
  if (!rawRules?.length) return [];

  return rawRules.map(normalizeLegacyTeamIdentifierRuleGroup);
}

export function teamHasTeamIdentifierRules(
  settings: TeamSettings | null | undefined
): boolean {
  return !!settings?.teamIdentifierRules?.length;
}

function normalizeTeamIdentifierValue(value: string): string {
  return value.trim().toUpperCase();
}

function fieldValueMatchesTeamIdentifierRegex(
  rawValue: string | undefined,
  pattern: string
): boolean {
  const trimmedPattern = pattern?.trim();
  if (!trimmedPattern) return false;

  const normalizedValue = normalizeTeamIdentifierValue(rawValue ?? '');
  try {
    return new RegExp(trimmedPattern, 'i').test(normalizedValue);
  } catch {
    return false;
  }
}

export function teamMatchesTeamIdentifierRules({
  getFieldValue,
  settings,
}: {
  getFieldValue: (fieldPath: string) => string | undefined;
  settings: TeamSettings;
}): boolean {
  const rules = getTeamIdentifierRulesFromTeamSettings(settings);
  if (rules.length === 0) return false;

  return rules.some((group) =>
    Object.entries(group).every(([fieldPath, regex]) =>
      fieldValueMatchesTeamIdentifierRegex(getFieldValue(fieldPath), regex)
    )
  );
}
